import { useEffect, useMemo } from 'react';

import { useToast } from '@aicon-dev/caas-ui';
import { useTranslation } from 'react-i18next';

import apiClient from '~/config/apiClient';

import { UploadFileTemps, UploadProps } from '../types';

import { useUploadTemps } from './useUploadTemps';

export const useUploadCall = (props: UploadProps) => {
  const {
    fileList,
    metadata,
    onlyImage,
    onAppend = () => {},
    onChange = () => {},
    customUploadApi,
  } = props;
  const { t } = useTranslation('global');
  const toast = useToast();
  const uploadTempsResponses = useUploadTemps(props);
  const { standbyFiles, fileTemps, modifyFileTemps, snycFileTemps, resetStandbyFiles } =
    uploadTempsResponses;

  /**
   * fileTemps가 변경되면 준비상태의 row를 찾아 업로드 진행
   */
  useEffect(() => {
    handleUpload(standbyFiles);
  }, [standbyFiles]);

  // 전체 로딩 여부
  const isLoading = useMemo(() => {
    const uploadingFiles = fileTemps.filter((e) => e.status !== 'done');
    return uploadingFiles.length > 0;
  }, [standbyFiles]);

  /**
   * Upload API
   */
  const handleUpload = async (standbyFiles: UploadFileTemps) => {
    if (standbyFiles.length === 0) return;
    const newFileListSlot = [];

    for (let i = 0; i < standbyFiles.length; i++) {
      const currentFileData = standbyFiles[i];

      // row를 로딩 상태로 변경
      modifyFileTemps(currentFileData.id, { status: 'uploading' });

      try {
        const currentFile = currentFileData.file as File;

        let data = [];
        if (customUploadApi) {
          const res = await customUploadApi(currentFile);
          data = res;
        } else {
          const formData = new FormData();
          formData.append('files', currentFile);

          if (metadata) {
            formData.append('isEnabled', 'false');
            formData.append('project_id', metadata.project_id);
            formData.append('metadataType', metadata.metadataType);
          }
          if (metadata?.metadata) {
            formData.append('metadata', metadata.metadata);
          }

          if (onlyImage && !currentFile.type.includes('image')) {
            toast.error(t('global:upload_can_only_image_format'));
            snycFileTemps();
            return;
          }

          const ApiPath = metadata ? `/files/upload-files` : `/upload/files`;

          const res = await apiClient.post(ApiPath, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          data = res.data;
        }

        // 로딩 완료 처리
        modifyFileTemps(currentFileData.id, {
          response: data?.[0],
          status: 'done',
        });

        newFileListSlot.push(data?.[0]);
        if (onAppend) onAppend(data?.[0]);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        console.error(err.response);
      }
    }

    if (onChange) onChange([...fileList, ...newFileListSlot]);
    resetStandbyFiles();
  };

  return {
    uploadTempsResponses,
    handleUpload,
    isLoading,
  };
};
